import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { useAuth } from './AuthContext';
import { consumePostLoginNext } from './postLoginNext';
import { homeForRole } from './externalRoles';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { completeGoogleLogin } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    // Scrubbed first, before anything else runs — an authorization code
    // sitting in the URL is a live, single-use credential that should not
    // linger for a page-view logger or browser history to catch.
    const rawSearch = window.location.search;
    window.history.replaceState(null, '', window.location.pathname);

    const params = new URLSearchParams(rawSearch);
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem('google_oauth_state');
    sessionStorage.removeItem('google_oauth_state');

    if (!code || !state || state !== expectedState) {
      navigate('/portal/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const redirect_uri = window.location.origin + '/portal/auth/callback';
        const response = await api.post('/auth/google/callback', { code, redirect_uri });
        const { data } = response;
        if (response.status === 202) {
          navigate('/portal/login', {
            replace: true,
            state: { mfa: { mfaToken: data.mfa_token, enrolled: data.enrolled } },
          });
          return;
        }
        const u = completeGoogleLogin(data);
        const next = consumePostLoginNext();
        const dest = next || homeForRole(u.role);
        navigate(dest, { replace: true });
      } catch (e) {
        // Don't log `e` — the axios error's request config carries the code.
        console.error('Google sign-in failed');
        // A 403 is the API telling the visitor something (invite-only, ticket
        // 117); pass its message through instead of a silent bounce.
        const detail = e?.response?.status === 403 ? e?.response?.data?.detail : null;
        navigate('/portal/login', { replace: true, state: detail ? { error: detail } : undefined });
      }
    })();
  }, [navigate, completeGoogleLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f2' }}>
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-[#8DC63F] rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-slate-600 font-medium">Signing you in...</p>
      </div>
    </div>
  );
}
